/**
 * Schedule: how often the watcher runs a rule, and how much data each run reads.
 *
 * Operational, not meaning: the same rule hourly or daily finds the same
 * episodes, only sooner or later. So a change here is not a new version and
 * needs no new backtest (migration 0032, `backend/argus_schedule.py`). On a
 * deployed rule it is the leader's call - "scheduling is a privilege" - and the
 * database refuses anyone else; this panel shows what it said.
 *
 * Nothing is saved until **Apply**: a clock somebody else's inbox runs on
 * should not move because a select was brushed.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { ref } from 'lit/directives/ref.js';
import { customElement, property, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import type { ArgusRule, ArgusSchedule } from '../../api/types.ts';
import type { SelectOption } from '../../ui/plain-select.ts';
import '../../ui/plain-select.ts';
import { setSchedule, sources } from './argus-rules-store.ts';
import { renderSchedulePreview } from './argus-schedule-preview.ts';

export const EVERY: ReadonlyArray<{ value: string; label: string }> = [
  { value: '5m', label: '5 minutes' },
  { value: '15m', label: '15 minutes' },
  { value: '30m', label: '30 minutes' },
  { value: '1h', label: 'hour' },
  { value: '3h', label: '3 hours' },
  { value: '6h', label: '6 hours' },
  { value: '12h', label: '12 hours' },
  { value: '1d', label: 'day' },
  { value: '7d', label: 'week (Mondays)' },
];

export const LOOKBACK: ReadonlyArray<{ value: string; label: string }> = [
  { value: '15m', label: '15 minutes' },
  { value: '30m', label: '30 minutes' },
  { value: '1h', label: '1 hour' },
  { value: '6h', label: '6 hours' },
  { value: '12h', label: '12 hours' },
  { value: '1d', label: '1 day' },
  { value: '2d', label: '2 days' },
  { value: '3d', label: '3 days' },
  { value: '7d', label: '7 days' },
  { value: '14d', label: '14 days' },
  { value: '30d', label: '30 days' },
];

/** Times of day on the half hour; one set some other way is kept as it is. */
function timesOfDay(current: string): SelectOption[] {
  const out: SelectOption[] = [];
  for (let m = 0; m < 24 * 60; m += 30) {
    const t = `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
    out.push({ value: t, label: t });
  }
  if (current && !out.some((o) => o.value === current)) {
    out.push({ value: current, label: current });
    out.sort((a, b) => a.value.localeCompare(b.value));
  }
  return out;
}

const SECONDS: Record<string, number> = { m: 60, h: 3600, d: 86400 };

export function durationSeconds(text: string): number {
  const match = /^(\d+)([mhd])$/.exec(text);
  return match ? Number(match[1]) * SECONDS[match[2]!]! : 0;
}

export const DEFAULT_SCHEDULE: ArgusSchedule = { every: '1d', at: '06:00', lookback: '7d' };

/**
 * A schedule the server will take, from what was picked: `at` only for daily
 * and weekly, and a lookback stretched to cover `every` - or data landing
 * between two runs would never be read.
 */
export function normaliseSchedule(s: ArgusSchedule): ArgusSchedule {
  const daily = durationSeconds(s.every) >= 86400;
  let lookback = s.lookback;
  if (durationSeconds(lookback) < durationSeconds(s.every)) {
    lookback = LOOKBACK.find((l) => durationSeconds(l.value) >= durationSeconds(s.every))?.value ?? '30d';
  }
  const out: ArgusSchedule = { every: s.every, lookback };
  if (daily) out.at = s.at || DEFAULT_SCHEDULE.at!;
  return out;
}

function same(a: ArgusSchedule, b: ArgusSchedule): boolean {
  return a.every === b.every && (a.at ?? '') === (b.at ?? '') && a.lookback === b.lookback;
}

/** "Every day at 06:00 UTC, reading the last 7 days" - the schedule as a sentence. */
export function scheduleWords(s: ArgusSchedule): string {
  const lookback = LOOKBACK.find((l) => l.value === s.lookback)?.label ?? s.lookback;
  const at = s.at ?? DEFAULT_SCHEDULE.at!;
  const every =
    s.every === '1d'
      ? `Every day at ${at} UTC`
      : s.every === '7d'
        ? `Every Monday at ${at} UTC`
        : `Every ${EVERY.find((e) => e.value === s.every)?.label ?? s.every}`;
  return `${every}, reading the last ${lookback}`;
}

export function when(iso: string | null | undefined): string {
  if (!iso) return '-';
  const t = new Date(iso);
  const days = Math.round((t.getTime() - Date.now()) / 86400000);
  const time = t.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  if (Math.abs(t.getTime() - Date.now()) < 60000) return 'now';
  if (days === 0 && t.getDate() === new Date().getDate()) return `today ${time}`;
  return `${t.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })} ${time}`;
}

@customElement('argus-schedule-panel')
export class ArgusSchedulePanel extends LightElement {
  @property({ attribute: false }) rule!: ArgusRule;
  /** May this person change it? Authors on a draft; the database has the last word on a deployed rule. */
  @property({ type: Boolean }) editable = false;
  /** One line - the schedule as a sentence and **Change** - until Change is
   *  pressed; the drawer's form. The pickers show only while changing. */
  @property({ type: Boolean }) summary = false;
  /** The editor's zone: the form, and beside it the next week drawn. */
  @property({ type: Boolean }) wide = false;

  @state() private draft: ArgusSchedule | null = null;
  @state() private saving = false;
  @state() private error: string | null = null;
  @state() private changing = false;
  /** How wide the drawn week has to be, measured, so it fills its column. */
  @state() private previewWidth = 0;
  private forRule: string | null = null;
  private sizer: ResizeObserver | null = null;
  private sized: Element | null = null;

  /** What the rule's source allows (Live: from 5 minutes, at most 48 hours back). */
  private limits(): { fastest: string; history: number } {
    const src = sources.value.find((s) => s.key === this.rule.source_key);
    return { fastest: src?.fastest ?? '15m', history: src?.history_seconds ?? Infinity };
  }

  private measure = (el: Element | undefined): void => {
    if (!el || el === this.sized || typeof ResizeObserver === 'undefined') return;
    this.sizer?.disconnect();
    this.sized = el;
    this.sizer = new ResizeObserver(([entry]) => {
      const w = Math.floor(entry?.contentRect.width ?? 0);
      if (w && Math.abs(w - this.previewWidth) > 2) this.previewWidth = w;
    });
    this.sizer.observe(el);
  };

  override disconnectedCallback(): void {
    this.sizer?.disconnect();
    this.sizer = null;
    this.sized = null;
    super.disconnectedCallback();
  }

  private current(): ArgusSchedule {
    return this.rule.schedule ?? DEFAULT_SCHEDULE;
  }

  override willUpdate(): void {
    if (this.rule && this.rule.id !== this.forRule) {
      this.forRule = this.rule.id;
      this.draft = null;
      this.error = null;
      this.changing = false;
    }
  }

  private pick(patch: Partial<ArgusSchedule>): void {
    this.error = null;
    const next = normaliseSchedule({ ...(this.draft ?? this.current()), ...patch });
    this.draft = same(next, this.current()) ? null : next;
  }

  private async apply(): Promise<void> {
    if (!this.draft) return;
    this.saving = true;
    this.error = await setSchedule(this.rule.id, this.draft);
    this.saving = false;
    if (!this.error) {
      this.draft = null;
      this.changing = false;
    }
  }

  private cancel(): void {
    this.draft = null;
    this.error = null;
    this.changing = false;
  }

  private renderRuns(): TemplateResult {
    const rule = this.rule;
    const ranThis = rule.last_run_at && rule.last_run_version === rule.version;
    if (rule.status !== 'deployed') {
      return html`<p class="argus-lfaint">Runs on this clock once it is deployed.</p>`;
    }
    return html`<p class="argus-lfaint argus-sched-runs">
      <span>Next <b>${when(rule.next_run_at)}</b></span>
      <span
        >Last
        ${ranThis
          ? html`<b>${when(rule.last_run_at)}</b> · ${rule.last_run_entities ?? 0} read${rule.last_run_failed
                ? html`, <span class="is-warn">${rule.last_run_failed} could not be</span>`
                : nothing}`
          : html`<b>not yet</b>${rule.last_run_at ? ' at this version' : ''}`}</span
      >
    </p>`;
  }

  private renderSummary(): TemplateResult {
    return html`
      <section class="argus-sched is-summary" aria-label="Schedule">
        <div class="argus-sched-line">
          <h4>Schedule</h4>
          <span class="argus-sched-words">${scheduleWords(this.current())}</span>
          ${this.editable
            ? html`<button type="button" class="argus-lmore" @click=${() => (this.changing = true)}>Change</button>`
            : nothing}
        </div>
        ${this.renderRuns()}
      </section>
    `;
  }

  override render(): TemplateResult {
    if (this.summary && !this.changing) return this.renderSummary();
    const shown = this.draft ?? this.current();
    const daily = durationSeconds(shown.every) >= 86400;
    const off = !this.editable || this.saving;
    const form = html`
        <div class="argus-sched-grid">
          <span class="argus-sched-label">Runs every</span>
          <plain-select
            class="argus-sched-pick"
            label="Runs every"
            .options=${EVERY.filter((o) => durationSeconds(o.value) >= durationSeconds(this.limits().fastest))}
            .value=${shown.every}
            ?disabled=${off}
            .select=${(v: string) => this.pick({ every: v })}
          ></plain-select>
          ${daily
            ? html`<span class="argus-sched-label">At <span class="argus-lfaint">UTC</span></span>
                <plain-select
                  class="argus-sched-pick"
                  label="At, UTC"
                  searchable
                  searchPlaceholder="Type a time…"
                  .options=${timesOfDay(shown.at ?? DEFAULT_SCHEDULE.at!)}
                  .value=${shown.at ?? DEFAULT_SCHEDULE.at!}
                  ?disabled=${off}
                  .select=${(v: string) => this.pick({ at: v })}
                ></plain-select>`
            : nothing}
          <span class="argus-sched-label">Reading the last</span>
          <plain-select
            class="argus-sched-pick"
            label="Reading the last"
            .options=${LOOKBACK.filter(
              (o) =>
                durationSeconds(o.value) >= durationSeconds(shown.every) &&
                durationSeconds(o.value) <= this.limits().history,
            )}
            .value=${shown.lookback}
            ?disabled=${off}
            .select=${(v: string) => this.pick({ lookback: v })}
          ></plain-select>
        </div>
        ${this.draft || (this.summary && this.changing)
          ? html`<div class="argus-sched-actions">
              <button type="button" class="dash-btn" ?disabled=${this.saving || !this.draft} @click=${() => void this.apply()}>
                ${this.saving ? 'Applying…' : 'Apply'}
              </button>
              <button type="button" class="dash-btn" ?disabled=${this.saving} @click=${() => this.cancel()}>
                Cancel
              </button>
              ${this.rule.status === 'deployed'
                ? html`<span class="argus-lfaint">Takes effect from the watcher's next check.</span>`
                : nothing}
            </div>`
          : nothing}
        ${this.error ? html`<p class="argus-bt-note is-error" role="alert">${this.error}</p>` : nothing}
        ${this.renderRuns()}`;
    if (this.wide) {
      return html`<section class="argus-sched is-wide" aria-label="Schedule">
        <div class="argus-sched-form">
          <p class="argus-sched-words">${scheduleWords(shown)}</p>
          ${form}
        </div>
        <div class="argus-sched-canvas" ${ref(this.measure)}>
          ${renderSchedulePreview(shown, undefined, this.previewWidth || undefined)}
        </div>
      </section>`;
    }
    return html`
      <section class="argus-sched" aria-label="Schedule">
        <h4>Schedule</h4>
        ${form}
      </section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'argus-schedule-panel': ArgusSchedulePanel;
  }
}
